import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import LoadingSpinner from '../common/LoadingSpinner'
import { usePublicSettings } from '../../hooks/usePublicSettings'
import { titleCase } from '../../utils/accountApi'

const INPUT = 'w-full px-3 py-2 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent'

export default function Register() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [conflict, setConflict] = useState(false)   // the number/email may already have an account (e.g. from WhatsApp)
  const { register, user } = useAuth()
  const navigate = useNavigate()
  const settings = usePublicSettings()
  // Minimum password length: operational setting security.password_min_length (the server checks it too)
  const minPassword = settings?.['security.password_min_length']

  // Signed up (or already signed in) → home
  useEffect(() => {
    if (user) navigate('/', { replace: true })
  }, [user, navigate])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    if (minPassword && formData.password.length < minPassword) {
      setError(`Password must be at least ${minPassword} characters long`)
      return
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)
    try {
      // A taken email or phone gets the server's generic answer (it never says which, to prevent account lookups)
      const result = await register({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        password: formData.password,
        address: {
          label: 'Home',
          // Market defaults (settings market.default_city / market.country_name); editable later in the address book
          city: titleCase(settings?.['market.default_city']) || undefined,
          country: settings?.['market.country_name'] || undefined
        }
      })
      if (!result.success) {
        setError(result.error)
        setConflict(result.code === 'SIGNUP_CONFLICT')
      }
    } catch {
      setError('Registration failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4 safe-area-inset">
      <div className="w-full max-w-sm">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-slate-900">Create Account</h1>
            <p className="text-slate-600 mt-2">Join our service platform</p>
          </div>

          {error && (
            <div role="alert" className="mb-4 rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          {conflict && (
            <div className="mb-4 flex flex-col gap-2">
              <Link to="/login/whatsapp" state={{ phone: formData.phone.trim() }}
                className="block w-full text-center border border-slate-300 text-slate-900 py-3 px-4 rounded-xl font-medium hover:bg-slate-50 touch-manipulation">
                Sign in with a WhatsApp code
              </Link>
              <Link to="/forgot-password" className="text-center text-sm text-slate-700 hover:underline">Forgot your password?</Link>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" aria-label="Create account">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
              <input id="name" name="name" type="text" autoComplete="name" value={formData.name} onChange={handleChange}
                className={INPUT} placeholder="Enter your full name" required />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input id="email" name="email" type="email" autoComplete="email" value={formData.email} onChange={handleChange}
                className={INPUT} placeholder="Enter your email" required />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
              <input id="phone" name="phone" type="tel" autoComplete="tel" value={formData.phone} onChange={handleChange}
                className={INPUT}
                placeholder={settings?.['market.phone_country_code'] ? `+${settings['market.phone_country_code']} …` : 'Enter your phone number'}
                required />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">Password</label>
              <input id="password" name="password" type="password" autoComplete="new-password" value={formData.password} onChange={handleChange}
                className={INPUT} placeholder={minPassword ? `At least ${minPassword} characters` : 'Create a password'}
                minLength={minPassword || undefined} required />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700 mb-1">Confirm Password</label>
              <input id="confirmPassword" name="confirmPassword" type="password" autoComplete="new-password" value={formData.confirmPassword} onChange={handleChange}
                className={INPUT} placeholder="Confirm your password" required />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-slate-900 text-white py-3 px-4 rounded-xl font-medium hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation shadow-lg hover:shadow-xl transition-shadow"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2"><LoadingSpinner size="sm" /> Creating account…</span>
              ) : 'Create Account'}
            </button>
          </form>

          <div className="text-center mt-6">
            <p className="text-slate-600">
              Already have an account?{' '}
              <Link to="/login" className="text-slate-900 font-medium hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
